import { useState, useEffect, useMemo } from 'react';
import { useAuth, API, getActiveBackendUrl } from '../../App';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Key, 
  Webhook, 
  Plus, 
  Trash2, 
  Copy, 
  Check,
  Eye,
  EyeOff,
  Loader2,
  ExternalLink,
  Settings,
  Plug,
  Globe,
  Plane,
  Building2,
  Zap,
  AlertCircle,
  CheckCircle2,
  Clock
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';


export default function Integrations() {
  const { token } = useAuth();
  
  // Get backend URL lazily to avoid initialization order issues
  const BACKEND_URL = useMemo(() => getActiveBackendUrl(), []);
  
  const [activeTab, setActiveTab] = useState('api-keys');
  const [loading, setLoading] = useState(false);
  
  // API Keys state
  const [apiKeys, setApiKeys] = useState([]);
  const [newKeyDialogOpen, setNewKeyDialogOpen] = useState(false);
  const [newKeyData, setNewKeyData] = useState({ name: '', permissions: ['read'], expires_in_days: null });
  const [createdKey, setCreatedKey] = useState(null);
  const [copiedKey, setCopiedKey] = useState(false);
  
  // Webhooks state
  const [webhooks, setWebhooks] = useState([]);
  const [newWebhookDialogOpen, setNewWebhookDialogOpen] = useState(false);
  const [newWebhookData, setNewWebhookData] = useState({ 
    name: '', 
    url: '', 
    events: [],
    enabled: true 
  });
  
  // Integrations state
  const [integrations, setIntegrations] = useState([]);
  const [newIntegrationDialogOpen, setNewIntegrationDialogOpen] = useState(false);
  const [newIntegrationData, setNewIntegrationData] = useState({
    type: '',
    name: '',
    enabled: false,
    config: {}
  });
  
  // Delete confirmation
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);

  useEffect(() => {
    fetchApiKeys();
    fetchWebhooks();
    fetchIntegrations();
  }, []);

  const fetchApiKeys = async () => {
    try {
      const response = await axios.get(`${API}/integrations/api-keys`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setApiKeys(response.data);
    } catch (error) {
      console.error('Error fetching API keys:', error);
    }
  };

  const fetchWebhooks = async () => {
    try {
      const response = await axios.get(`${API}/integrations/webhooks`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setWebhooks(response.data);
    } catch (error) {
      console.error('Error fetching webhooks:', error);
    }
  };

  const fetchIntegrations = async () => {
    try {
      const response = await axios.get(`${API}/integrations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setIntegrations(response.data);
    } catch (error) {
      console.error('Error fetching integrations:', error);
    }
  };

  const createApiKey = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/integrations/api-keys`, newKeyData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCreatedKey(response.data);
      fetchApiKeys();
      toast.success('API key created successfully!');
    } catch (error) {
      console.error('Error creating API key:', error);
      toast.error('Failed to create API key');
    } finally {
      setLoading(false);
    }
  };

  const revokeApiKey = async (keyId) => {
    try {
      await axios.delete(`${API}/integrations/api-keys/${keyId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchApiKeys();
      toast.success('API key revoked');
    } catch (error) {
      console.error('Error revoking API key:', error);
      toast.error('Failed to revoke API key');
    }
  };

  const createWebhook = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/integrations/webhooks`, newWebhookData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchWebhooks();
      setNewWebhookDialogOpen(false);
      setNewWebhookData({ name: '', url: '', events: [], enabled: true });
      toast.success('Webhook created successfully!');
    } catch (error) {
      console.error('Error creating webhook:', error);
      toast.error('Failed to create webhook');
    } finally {
      setLoading(false);
    }
  };

  const deleteWebhook = async (webhookId) => {
    try {
      await axios.delete(`${API}/integrations/webhooks/${webhookId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchWebhooks();
      toast.success('Webhook deleted');
    } catch (error) {
      console.error('Error deleting webhook:', error);
      toast.error('Failed to delete webhook');
    }
  };

  const testWebhook = async (webhookId) => {
    try {
      const response = await axios.post(`${API}/integrations/webhooks/${webhookId}/test`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        toast.success(`Webhook test successful! Status: ${response.data.status_code}`);
      } else {
        toast.error(`Webhook test failed: ${response.data.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error testing webhook:', error);
      toast.error('Failed to test webhook');
    }
  };

  const createIntegration = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/integrations`, newIntegrationData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchIntegrations();
      setNewIntegrationDialogOpen(false);
      setNewIntegrationData({ type: '', name: '', enabled: false, config: {} });
      toast.success('Integration created successfully!');
    } catch (error) {
      console.error('Error creating integration:', error);
      toast.error('Failed to create integration');
    } finally {
      setLoading(false);
    }
  };

  const deleteIntegration = async (integrationId) => {
    try {
      await axios.delete(`${API}/integrations/${integrationId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchIntegrations();
      toast.success('Integration deleted');
    } catch (error) {
      console.error('Error deleting integration:', error);
      toast.error('Failed to delete integration');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopiedKey(true);
    setTimeout(() => setCopiedKey(false), 2000);
    toast.success('Copied to clipboard!');
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Never';
    return new Date(dateStr).toLocaleString();
  };

  const eventOptions = [
    { value: 'ride_created', label: 'Ride Created' },
    { value: 'ride_updated', label: 'Ride Updated' },
    { value: 'ride_completed', label: 'Ride Completed' },
    { value: 'ride_cancelled', label: 'Ride Cancelled' },
    { value: 'expense_created', label: 'Expense Created' }
  ];

  const integrationTypes = [
    { value: 'etg', label: 'ETG (Emerging Travel Group)', icon: Building2 },
    { value: 'flight_tracking', label: 'Flight Tracking', icon: Plane },
    { value: 'booking_platform', label: 'Booking Platform', icon: Globe },
    { value: 'custom', label: 'Custom Integration', icon: Plug }
  ];

  return (
    <div className="p-4 lg:p-8 pb-20 lg:pb-8">
      <div className="mb-8">
        <h1 className="text-2xl lg:text-3xl font-bold text-slate-900">Integrations</h1>
        <p className="text-slate-600 mt-2">Manage API keys, webhooks, and third-party integrations</p>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6 border-b border-slate-200">
        <button
          onClick={() => setActiveTab('api-keys')}
          className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors ${
            activeTab === 'api-keys' 
              ? 'border-blue-600 text-blue-600' 
              : 'border-transparent text-slate-600 hover:text-slate-900'
          }`}
        >
          <Key className="h-4 w-4 inline mr-2" />
          API Keys
        </button>
        <button
          onClick={() => setActiveTab('webhooks')}
          className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors ${
            activeTab === 'webhooks' 
              ? 'border-blue-600 text-blue-600' 
              : 'border-transparent text-slate-600 hover:text-slate-900'
          }`}
        >
          <Webhook className="h-4 w-4 inline mr-2" />
          Webhooks
        </button>
        <button
          onClick={() => setActiveTab('integrations')}
          className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors ${
            activeTab === 'integrations' 
              ? 'border-blue-600 text-blue-600' 
              : 'border-transparent text-slate-600 hover:text-slate-900'
          }`}
        >
          <Plug className="h-4 w-4 inline mr-2" />
          Third-Party
        </button>
      </div>

      {/* API Keys Tab */}
      {activeTab === 'api-keys' && (
        <div className="space-y-6">
          <div className="bg-white border border-slate-200 rounded-lg p-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-xl font-semibold text-slate-900">API Keys</h2>
                <p className="text-sm text-slate-500 mt-1">Create API keys for external access to your data</p>
              </div>
              <Dialog open={newKeyDialogOpen} onOpenChange={setNewKeyDialogOpen}>
                <DialogTrigger asChild>
                  <Button onClick={() => { setCreatedKey(null); setNewKeyData({ name: '', permissions: ['read'], expires_in_days: null }); }}>
                    <Plus className="h-4 w-4 mr-2" />
                    Create API Key
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>{createdKey ? 'API Key Created!' : 'Create New API Key'}</DialogTitle>
                  </DialogHeader>
                  
                  {createdKey ? (
                    <div className="space-y-4">
                      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                        <div className="flex items-start gap-2">
                          <AlertCircle className="h-5 w-5 text-amber-600 mt-0.5" />
                          <div>
                            <p className="font-medium text-amber-800">Save this key now!</p>
                            <p className="text-sm text-amber-700">You won't be able to see it again.</p>
                          </div>
                        </div>
                      </div>
                      
                      <div>
                        <Label className="text-sm text-slate-600">Your API Key</Label>
                        <div className="flex gap-2 mt-1">
                          <Input value={createdKey.api_key} readOnly className="font-mono text-sm" />
                          <Button 
                            variant="outline" 
                            onClick={() => copyToClipboard(createdKey.api_key)}
                          >
                            {copiedKey ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                          </Button>
                        </div>
                      </div>
                      
                      <Button onClick={() => { setNewKeyDialogOpen(false); setCreatedKey(null); }} className="w-full">
                        Done
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <div>
                        <Label>Key Name</Label>
                        <Input
                          value={newKeyData.name}
                          onChange={(e) => setNewKeyData({ ...newKeyData, name: e.target.value })}
                          placeholder="e.g., Production API"
                        />
                      </div>
                      
                      <div>
                        <Label>Permissions</Label>
                        <div className="space-y-2 mt-2">
                          {['read', 'write', 'delete'].map((perm) => (
                            <label key={perm} className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={newKeyData.permissions.includes(perm)}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setNewKeyData({ ...newKeyData, permissions: [...newKeyData.permissions, perm] });
                                  } else {
                                    setNewKeyData({ ...newKeyData, permissions: newKeyData.permissions.filter(p => p !== perm) });
                                  }
                                }}
                                className="rounded border-slate-300"
                              />
                              <span className="text-sm capitalize">{perm}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                      
                      <div>
                        <Label>Expiration (optional)</Label>
                        <Select
                          value={newKeyData.expires_in_days?.toString() || 'never'}
                          onValueChange={(v) => setNewKeyData({ ...newKeyData, expires_in_days: v === 'never' ? null : parseInt(v) })}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="never">Never expires</SelectItem>
                            <SelectItem value="30">30 days</SelectItem>
                            <SelectItem value="90">90 days</SelectItem>
                            <SelectItem value="365">1 year</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      
                      <Button onClick={createApiKey} disabled={loading || !newKeyData.name} className="w-full">
                        {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Key className="h-4 w-4 mr-2" />}
                        Create Key
                      </Button>
                    </div>
                  )}
                </DialogContent>
              </Dialog>
            </div>

            {/* API Keys List */}
            {apiKeys.length === 0 ? (
              <div className="text-center py-12 text-slate-500">
                <Key className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>No API keys created yet</p>
                <p className="text-sm">Create an API key to allow external access</p>
              </div>
            ) : (
              <div className="space-y-3">
                {apiKeys.map((key) => (
                  <div key={key.id} className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-slate-900">{key.name}</span>
                        <code className="text-xs bg-slate-200 px-2 py-0.5 rounded">{key.key_prefix}</code>
                      </div>
                      <div className="flex items-center gap-4 mt-1 text-xs text-slate-500">
                        <span>Permissions: {key.permissions?.join(', ')}</span>
                        <span>Created: {formatDate(key.created_at)}</span>
                        {key.last_used && <span>Last used: {formatDate(key.last_used)}</span>}
                        {key.expires_at && <span>Expires: {formatDate(key.expires_at)}</span>}
                      </div>
                    </div>
                    <Button variant="destructive" size="sm" onClick={() => revokeApiKey(key.id)}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}

            {/* API Documentation */}
            <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <h3 className="font-medium text-blue-900 mb-2">API Endpoints</h3>
              <div className="space-y-2 text-sm text-blue-800 font-mono">
                <p>GET {BACKEND_URL}/api/external/rides</p>
                <p>POST {BACKEND_URL}/api/external/rides</p>
                <p>GET {BACKEND_URL}/api/external/statistics</p>
              </div>
              <p className="text-xs text-blue-600 mt-2">Include header: X-API-Key: your_api_key</p>
            </div>
          </div>
        </div>
      )}

      {/* Webhooks Tab */}
      {activeTab === 'webhooks' && (
        <div className="space-y-6">
          <div className="bg-white border border-slate-200 rounded-lg p-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-xl font-semibold text-slate-900">Webhooks</h2>
                <p className="text-sm text-slate-500 mt-1">Send real-time notifications to external services</p>
              </div>
              <Dialog open={newWebhookDialogOpen} onOpenChange={setNewWebhookDialogOpen}>
                <DialogTrigger asChild>
                  <Button>
                    <Plus className="h-4 w-4 mr-2" />
                    Create Webhook
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Create New Webhook</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div>
                      <Label>Name</Label>
                      <Input
                        value={newWebhookData.name}
                        onChange={(e) => setNewWebhookData({ ...newWebhookData, name: e.target.value })}
                        placeholder="e.g., Slack Notifications"
                      />
                    </div>
                    
                    <div>
                      <Label>Webhook URL</Label>
                      <Input
                        value={newWebhookData.url}
                        onChange={(e) => setNewWebhookData({ ...newWebhookData, url: e.target.value })}
                        placeholder="https://your-service.com/webhook"
                      />
                    </div>
                    
                    <div>
                      <Label>Events to trigger</Label>
                      <div className="space-y-2 mt-2">
                        {eventOptions.map((event) => (
                          <label key={event.value} className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={newWebhookData.events.includes(event.value)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setNewWebhookData({ ...newWebhookData, events: [...newWebhookData.events, event.value] });
                                } else {
                                  setNewWebhookData({ ...newWebhookData, events: newWebhookData.events.filter(ev => ev !== event.value) });
                                }
                              }}
                              className="rounded border-slate-300"
                            />
                            <span className="text-sm">{event.label}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                    
                    <Button onClick={createWebhook} disabled={loading || !newWebhookData.name || !newWebhookData.url} className="w-full">
                      {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Webhook className="h-4 w-4 mr-2" />}
                      Create Webhook
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            </div>

            {/* Webhooks List */}
            {webhooks.length === 0 ? (
              <div className="text-center py-12 text-slate-500">
                <Webhook className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>No webhooks configured yet</p>
                <p className="text-sm">Create a webhook to send data to external services</p>
              </div>
            ) : (
              <div className="space-y-3">
                {webhooks.map((webhook) => (
                  <div key={webhook.id} className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-slate-900">{webhook.name}</span>
                        {webhook.enabled ? (
                          <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">Active</span>
                        ) : (
                          <span className="text-xs bg-slate-200 text-slate-600 px-2 py-0.5 rounded-full">Disabled</span>
                        )}
                      </div>
                      <p className="text-sm text-slate-500 mt-1 font-mono">{webhook.url}</p>
                      <p className="text-xs text-slate-400 mt-1">Events: {webhook.events?.join(', ')}</p>
                    </div>
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" onClick={() => testWebhook(webhook.id)}>
                        <Zap className="h-4 w-4 mr-1" />
                        Test
                      </Button>
                      <Button variant="destructive" size="sm" onClick={() => deleteWebhook(webhook.id)}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Third-Party Integrations Tab */}
      {activeTab === 'integrations' && (
        <div className="space-y-6">
          <div className="bg-white border border-slate-200 rounded-lg p-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-xl font-semibold text-slate-900">Third-Party Integrations</h2>
                <p className="text-sm text-slate-500 mt-1">Connect to external services and platforms</p>
              </div>
              <Dialog open={newIntegrationDialogOpen} onOpenChange={setNewIntegrationDialogOpen}>
                <DialogTrigger asChild>
                  <Button>
                    <Plus className="h-4 w-4 mr-2" />
                    Add Integration
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Add New Integration</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div>
                      <Label>Integration Type</Label>
                      <Select
                        value={newIntegrationData.type}
                        onValueChange={(v) => setNewIntegrationData({ ...newIntegrationData, type: v })}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select integration type" />
                        </SelectTrigger>
                        <SelectContent>
                          {integrationTypes.map((type) => (
                            <SelectItem key={type.value} value={type.value}>
                              <div className="flex items-center gap-2">
                                <type.icon className="h-4 w-4" />
                                {type.label}
                              </div>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div>
                      <Label>Name</Label>
                      <Input
                        value={newIntegrationData.name}
                        onChange={(e) => setNewIntegrationData({ ...newIntegrationData, name: e.target.value })}
                        placeholder="e.g., ETG Production"
                      />
                    </div>

                    {newIntegrationData.type === 'etg' && (
                      <>
                        <div>
                          <Label>ETG API Key</Label>
                          <Input
                            value={newIntegrationData.config.api_key || ''}
                            onChange={(e) => setNewIntegrationData({ 
                              ...newIntegrationData, 
                              config: { ...newIntegrationData.config, api_key: e.target.value }
                            })}
                            placeholder="Enter ETG API key"
                          />
                        </div>
                        <div>
                          <Label>ETG API Endpoint</Label>
                          <Input
                            value={newIntegrationData.config.endpoint || ''}
                            onChange={(e) => setNewIntegrationData({ 
                              ...newIntegrationData, 
                              config: { ...newIntegrationData.config, endpoint: e.target.value }
                            })}
                            placeholder="https://api.etg.travel/v1"
                          />
                        </div>
                      </>
                    )}

                    {newIntegrationData.type === 'flight_tracking' && (
                      <>
                        <div>
                          <Label>Provider</Label>
                          <Select
                            value={newIntegrationData.config.provider || ''}
                            onValueChange={(v) => setNewIntegrationData({ 
                              ...newIntegrationData, 
                              config: { ...newIntegrationData.config, provider: v }
                            })}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Select provider" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="aviationstack">AviationStack (Free tier)</SelectItem>
                              <SelectItem value="flightaware">FlightAware</SelectItem>
                              <SelectItem value="flightradar24">FlightRadar24</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label>API Key</Label>
                          <Input
                            value={newIntegrationData.config.api_key || ''}
                            onChange={(e) => setNewIntegrationData({ 
                              ...newIntegrationData, 
                              config: { ...newIntegrationData.config, api_key: e.target.value }
                            })}
                            placeholder="Enter API key"
                          />
                        </div>
                      </>
                    )}

                    {newIntegrationData.type === 'booking_platform' && (
                      <>
                        <div>
                          <Label>Platform</Label>
                          <Select
                            value={newIntegrationData.config.platform || ''}
                            onValueChange={(v) => setNewIntegrationData({ 
                              ...newIntegrationData, 
                              config: { ...newIntegrationData.config, platform: v }
                            })}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Select platform" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="getyourguide">GetYourGuide</SelectItem>
                              <SelectItem value="viator">Viator</SelectItem>
                              <SelectItem value="klook">Klook</SelectItem>
                              <SelectItem value="other">Other</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label>API Key</Label>
                          <Input
                            value={newIntegrationData.config.api_key || ''}
                            onChange={(e) => setNewIntegrationData({ 
                              ...newIntegrationData, 
                              config: { ...newIntegrationData.config, api_key: e.target.value }
                            })}
                            placeholder="Enter API key"
                          />
                        </div>
                      </>
                    )}

                    {newIntegrationData.type === 'custom' && (
                      <>
                        <div>
                          <Label>API Endpoint</Label>
                          <Input
                            value={newIntegrationData.config.endpoint || ''}
                            onChange={(e) => setNewIntegrationData({ 
                              ...newIntegrationData, 
                              config: { ...newIntegrationData.config, endpoint: e.target.value }
                            })}
                            placeholder="https://api.example.com"
                          />
                        </div>
                        <div>
                          <Label>API Key (optional)</Label>
                          <Input
                            value={newIntegrationData.config.api_key || ''}
                            onChange={(e) => setNewIntegrationData({ 
                              ...newIntegrationData, 
                              config: { ...newIntegrationData.config, api_key: e.target.value }
                            })}
                            placeholder="Enter API key if required"
                          />
                        </div>
                      </>
                    )}
                    
                    <div className="flex items-center gap-2">
                      <Switch
                        checked={newIntegrationData.enabled}
                        onCheckedChange={(checked) => setNewIntegrationData({ ...newIntegrationData, enabled: checked })}
                      />
                      <Label>Enable integration</Label>
                    </div>
                    
                    <Button onClick={createIntegration} disabled={loading || !newIntegrationData.type || !newIntegrationData.name} className="w-full">
                      {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Plug className="h-4 w-4 mr-2" />}
                      Add Integration
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            </div>

            {/* Available Integrations */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              {integrationTypes.map((type) => {
                const activeIntegration = integrations.find(i => i.type === type.value && i.enabled);
                return (
                  <div key={type.value} className={`p-4 rounded-lg border ${activeIntegration ? 'border-green-200 bg-green-50' : 'border-slate-200 bg-slate-50'}`}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`h-10 w-10 rounded-lg flex items-center justify-center ${activeIntegration ? 'bg-green-100' : 'bg-slate-200'}`}>
                          <type.icon className={`h-5 w-5 ${activeIntegration ? 'text-green-600' : 'text-slate-500'}`} />
                        </div>
                        <div>
                          <p className="font-medium text-slate-900">{type.label}</p>
                          <p className="text-xs text-slate-500">
                            {activeIntegration ? `Connected: ${activeIntegration.name}` : 'Not configured'}
                          </p>
                        </div>
                      </div>
                      {activeIntegration ? (
                        <CheckCircle2 className="h-5 w-5 text-green-500" />
                      ) : (
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => {
                            setNewIntegrationData({ ...newIntegrationData, type: type.value });
                            setNewIntegrationDialogOpen(true);
                          }}
                        >
                          Setup
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Configured Integrations List */}
            {integrations.length > 0 && (
              <div className="border-t border-slate-200 pt-6">
                <h3 className="font-medium text-slate-900 mb-4">Configured Integrations</h3>
                <div className="space-y-3">
                  {integrations.map((integration) => {
                    const typeInfo = integrationTypes.find(t => t.value === integration.type);
                    const Icon = typeInfo?.icon || Plug;
                    return (
                      <div key={integration.id} className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                        <div className="flex items-center gap-3">
                          <Icon className="h-5 w-5 text-slate-600" />
                          <div>
                            <span className="font-medium text-slate-900">{integration.name}</span>
                            {integration.enabled ? (
                              <span className="ml-2 text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">Active</span>
                            ) : (
                              <span className="ml-2 text-xs bg-slate-200 text-slate-600 px-2 py-0.5 rounded-full">Disabled</span>
                            )}
                            <p className="text-xs text-slate-500 mt-1">{typeInfo?.label}</p>
                          </div>
                        </div>
                        <Button variant="destructive" size="sm" onClick={() => deleteIntegration(integration.id)}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
